import { useCallback, useEffect, useState } from "react";
import {
  extractUtmFromUrl,
  getInitialUtmParams,
  getUtmFromStorage,
  hasUtmParams,
  saveUtmToStorage,
  type UtmParams,
} from "@/utils/utm";

/**
 * Hook to persist UTM parameters during the entire session of the user.
 * Reads any UTM parameter from the query string and saves them in sessionStorage.
 * UTM parameters are kept in storage for tracking purposes but not restored to URL.
 */
export function useUtmPersistence() {
  const [utmParams, setUtmParams] = useState<UtmParams>(() => getInitialUtmParams());

  // Get all query parameters that start with 'utm_'
  const getAllUtmFromQuery = useCallback((): UtmParams => {
    if (typeof window === "undefined") {
      return {};
    }

    const searchParams = new URLSearchParams(window.location.search);
    return extractUtmFromUrl(searchParams);
  }, []);

  const handleUtmFromQuery = useCallback((): void => {
    const queryUtmParams = getAllUtmFromQuery();

    if (hasUtmParams(queryUtmParams)) {
      setUtmParams(queryUtmParams);
      saveUtmToStorage(queryUtmParams);
    }
  }, [getAllUtmFromQuery]);

  const handleUtmFromStorage = useCallback((): void => {
    const storedParams = getUtmFromStorage();
    if (!storedParams) {
      return;
    }

    setUtmParams(storedParams);
  }, []);

  useEffect(() => {
    const queryUtmParams = getAllUtmFromQuery();
    const hasUtmInQuery = hasUtmParams(queryUtmParams);

    if (hasUtmInQuery) {
      handleUtmFromQuery();
      return;
    }

    handleUtmFromStorage();
  }, [getAllUtmFromQuery, handleUtmFromQuery, handleUtmFromStorage]);

  return {
    utmParams,
    // Legacy support for backward compatibility
    utmSource: utmParams.utm_source,
    utmMedium: utmParams.utm_medium,
    utmCampaign: utmParams.utm_campaign,
  };
}
